import { motion } from "framer-motion";
import { Helmet } from "react-helmet";
import { Link } from "wouter";
import { Button } from "@/components/ui/button";
import PageBanner from "@/components/PageBanner";
import PaymentPartners from "@/components/PaymentPartners";
import OptimizedImage from "@/components/OptimizedImage";
import { Compass, Camera, Users, Mountain, MapPin, Clock } from "lucide-react";
import { useLanguage } from "@/lib/LanguageContext";

const nationalParks = [
  {
    name: "Bwindi Impenetrable National Park",
    description: "A UNESCO World Heritage Site located in southwestern Uganda. The park is home to over 400 mountain gorillas, making it an ideal destination for gorilla trekking. The park's dense forest and diverse wildlife make it a nature lover's paradise.",
  image: "/gorilla.webp",
    activities: [
      "Gorilla trekking",
      "Bird watching",
      "Nature walks",
      "Community visits",
      "Hiking",
      "Mountain biking",
      "Forest walks",
      "Guided tours"
    ]
  },
  {
    name: "Kibale National Park",
    description: "Known for its diverse primate population, including over 1,450 chimpanzees, as well as other primates like monkeys and baboons. The park's tropical rainforest is also home to a wide variety of bird species.",
    image: "/chimpazee.png",
    activities: [
      "Chimpanzee trekking",
      "Bird watching",
      "Nature walks",
      "Community visits",
      "Hiking",
      "Primate watching",
      "Guided tours",
      "Swamp walks"
    ]
  },
  {
    name: "Murchison Falls National Park",
    description: "Uganda's largest national park, home to over 76 species of mammals, including lions, elephants, and giraffes. The park's main attraction is the Murchison Falls, where the Nile River flows through a narrow gorge.",
    image: "/murchison-falls.jpg",
    activities: [
      "Game drives",
      "Boat cruises",
      "Hiking",
      "Bird watching",
      "Fishing",
      "Cultural visits",
      "Guided tours",
      "Sportfishing"
    ]
  },
  {
    name: "Queen Elizabeth National Park",
    description: "Known for its diverse ecosystems and home to over 600 species of birds and various mammals including lions, elephants, and hippos. The Kazinga Channel is a major attraction for boat cruises.",
    image: "/5O5A0010.png",
    activities: [
      "Game drives",
      "Boat cruises",
      "Chimpanzee trekking",
      "Bird watching",
      "Hiking",
      "Cultural visits",
      "Guided tours",
      "Lion tracking"
    ]
  },
  {
    name: "Mgahinga Gorilla National Park",
    description: "Located in southwestern Uganda and is home to a small population of mountain gorillas. The park is also known for its volcanic landscapes and diverse wildlife.",
    image: "/gorillatrekking.png",
    activities: [
      "Gorilla trekking",
      "Hiking",
      "Bird watching",
      "Community visits",
      "Volcano climbing",
      "Guided tours",
      "Cave exploration"
    ]
  },
  {
    name: "Lake Mburo National Park",
    description: "Located in southwestern Uganda and is the country's smallest national park. The park is home to a wide variety of wildlife, including zebras, impalas, and hippos.",
    image: "/Lake-Mburo-Birds.webp",
    activities: [
      "Game drives",
      "Boat cruises",
      "Hiking",
      "Bird watching",
      "Fishing",
      "Horseback riding",
      "Guided tours",
      "Community visits"
    ]
  },
  {
    name: "Rwenzori Mountains National Park",
    description: "Located in western Uganda and known for its rugged mountain landscapes and diverse wildlife. The park is home to over 70 species of mammals, including elephants, buffaloes, and monkeys.",
    image: "/rwenzori.jpg",
    activities: [
      "Mountain climbing",
      "Hiking",
      "Bird watching",
      "Nature walks",
      "Community visits",
      "Guided tours",
      "Scenic drives"
    ]
  },
  {
    name: "Semuliki National Park",
    description: "Located in western Uganda and known for its diverse wildlife and scenic landscapes. The park is home to over 400 species of birds, as well as a wide variety of mammals, including monkeys and baboons.",
    image: "/5O5A9186.JPG",
    activities: [
      "Hiking",
      "Bird watching",
      "Nature walks",
      "Community visits",
      "Cultural tours",
      "Guided tours",
      "Hot springs visits"
    ]
  },
  {
    name: "Mount Elgon National Park",
    description: "Located in eastern Uganda and known for its rugged mountain landscapes and diverse wildlife. The park is home to over 40 species of mammals, including elephants, buffaloes, and duikers. The park's highest peak, Wagagai, rises to 4,321 meters above sea level.",
    image: "/Elgon-plains.webp",
    activities: [
      "Hiking",
      "Mountain climbing",
      "Bird watching",
      "Nature walks",
      "Community visits",
      "Guided tours",
      "Cave exploration",
      "Rock climbing",
      "Abseiling"
    ]
  }
];

const wildlifeReserves = [
  {
    name: "Ajai Wildlife Reserve",
    description: "Located in northwestern Uganda, Ajai Wildlife Reserve covers 166 square kilometers of grass plains, sparse savannah woodland, and swamps. The reserve is home to various birds, including Marabou stocks, African Fish eagles, and Grey-crowned cranes.",
    image: "/crested-crane.jpg",
    activities: ["Nature walks", "Game drives", "Bird watching"],
    wildlife: ["Uganda Kobs", "Hartebeests", "Warthogs", "Hippopotamuses", "Leopards"]
  },
  {
    name: "Bokora Corridor Wildlife Reserve",
    description: "Located in northeastern Uganda, Bokora Corridor Wildlife Reserve covers 2,056 square kilometers of rugged terrain. The reserve is known for its unique bird species and is home to Rothschild's Giraffe, Spotted Hyenas, and Leopards.",
    image: "/Bird_bokora.webp",
    activities: ["Bird watching", "Hikes", "Community visits"],
    wildlife: ["Rothschild's Giraffe", "Spotted Hyenas", "Leopards", "Topis", "Oryx"]
  },
  {
    name: "Bugungu Wildlife Reserve",
    description: "Located near Murchison Falls National Park, Bugungu Wildlife Reserve covers 473 square kilometers of tropical forest. The reserve is home to 600 Uganda Kobs, 1,200 Oribis, and 240 bird species.",
    image: "/baboons-bugungu.webp",
    activities: ["Game drives", "Nature walks", "Bird watching"],
    wildlife: ["Uganda Kobs", "Oribis", "Duikers", "Bushbucks"]
  },
  {
    name: "Kabwoya Wildlife Reserve",
    description: "Located near Lake Albert, Kabwoya Wildlife Reserve covers 87 square miles of rolling hills and wooded grasslands. The reserve is known for its fishing opportunities and is home to various bird species.",
    image: "/Kabwoya_cob.webp",
    activities: ["Guided hikes", "Nature walks", "Mountain biking", "Fishing"],
    wildlife: ["Hippopotamuses", "Crocodiles", "Fish species"]
  },
  {
    name: "Karuma Wildlife Reserve",
    description: "Located near Murchison Falls National Park, Karuma Wildlife Reserve is known for its waterfalls and abundant wildlife. The reserve is home to various mammals, including Buffaloes, Leopards, and Elephants.",
    image: "/buffalo.png",
    activities: ["Game drives", "Bird watching", "Nature Walks", "Fishing"],
    wildlife: ["Buffaloes", "Leopards", "Elephants", "Uganda Kobs"]
  },
  {
    name: "Katonga Wildlife Reserve",
    description: "Located on the banks of the Katonga River, Katonga Wildlife Reserve is home to 40 mammal species, including the shy Sitatunga Antelope. The reserve is known for its wetland habitats and is a great spot for bird watching.",
    image: "/katonga_wildreserve.webp",
    activities: ["Guided nature walks", "hikes", "canoe safaris"],
    wildlife: ["Sitatunga Antelope", "African Elephants", "Uganda Kobs"]
  },
  {
    name: "Kigezi Wildlife Reserve",
    description: "Located in western Uganda, Kigezi Wildlife Reserve covers 265 square kilometers of rolling hills and wooded grasslands. The reserve is home to African elephants, Kobs, Buffaloes, and various bird species.",
    image: "/waterbuck-kigezi.webp",
    activities: ["Game drives", "bird watching", "nature walks"],
    wildlife: ["African Elephants", "Kobs", "Buffaloes", "Giant Forest Hogs"]
  },
  {
    name: "Kyambura Wildlife Reserve",
    description: "Located near Queen Elizabeth National Park, Kyambura Wildlife Reserve is known for its chimpanzee population. The reserve is home to various primates, including Chimpanzees, Black-and-white Colobus Monkeys, and Red-tailed Monkeys.",
    image: "/Lions-Kyambura.webp",
    activities: ["Chimpanzee trekking", "bird watching", " guided nature walks"],
    wildlife: ["Chimpanzees", "Black-and-white Colobus Monkeys", "Red-tailed Monkeys"]
  },
  {
    name: "East Madi Wildlife Reserve",
    description: "Located in northwestern Uganda, East Madi Wildlife Reserve covers 831 square kilometers of tropical forest. The reserve is home to 50 mammals, 181 bird species, and 374 plant species.",
    image: "/Grey-Crowned-crane.webp",
    activities: ["Nature walks", "wildlife viewing", "bird watching"],
    wildlife: ["African Elephants", "Buffaloes", "Uganda Kobs", "Duikers"]
  },
  {
    name: "Matheniko Wildlife Reserve",
    description: "Located in northeastern Uganda, Matheniko Wildlife Reserve is home to the Lesser Kudu, Roan Antelopes, and various bird species. The reserve is known for its rugged terrain and is a great spot for hiking and nature walks.",
    image: "/bird_matheniko.webp",
    activities: ["Bird watching", "nature walks", "community visits"],
    wildlife: ["Lesser Kudu", "Roan Antelopes", "Ostriches", "Bright's Gazelle"]
  },
  {
    name: "Pian Upe Wildlife Reserve",
    description: "Located in northeastern Uganda, Pian Upe Wildlife Reserve is the second-largest conservation area in Uganda. The reserve is home to several mammals, including Roan antelopes, cheetahs, and Serval cats.",
    image: "/Pian-Upe-Wildlife-reserve.webp",
    activities: ["Wildlife safaris", "mountain climbing", "cultural community visits"],
    wildlife: ["Elands", "Roan Antelopes", "cheetahs", "Serval cats"]
  },
  {
    name: "Toro-Semliki Wildlife Reserve",
    description: "Located in western Uganda, Toro-Semliki Wildlife Reserve is known for its abundant birdlife, with over 400 species for example; Abyssinian ground hornbill, Malachite kingfisher, African. The reserve is home to various mammals, including Buffaloes, Uganda Kobs, and Elephants.",
    image: "/242-Uganda-Kob.webp",
    activities: [": Boat rides on Lake Albert", "bird watching", "game drives"],
    wildlife: ["Buffaloes", "Uganda Kobs", "Elephants", "Hippopotamuses", "Crocodiles"]
  },
];

const otherAttractions = [
  {
    name: "Ziwa Rhino Sanctuary",
    description: "Located in Nakasongola district and is home to southern white rhinos. The sanctuary is an effort to reintroduce rhinos into Uganda's national parks.",
    image: "/white-rhino-ziwa-rhino-wildlife-ranch.webp",
    activities: [
      "Rhino trekking",
      "Bird watching",
      "Nature walks",
      "Community visits",
      "Guided tours"
    ]
  },
  {
    name: "Source of the River Nile",
    description: "The source of the River Nile is located in Jinja, Uganda. The Nile River flows through 11 countries before emptying into the Mediterranean Sea.",
    image: "/source-of-the-river-nile.webp",
    activities: [
      "Boat cruises",
      "Fishing",
      "Community visits",
      "Cultural tours",
      "White-water rafting",
      "Kayaking",
      "Bungee jumping"
    ]
  },
  {
    name: "Lake Bunyonyi",
    description: "Lake Bunyonyi is located in southwestern Uganda and is believed to be the second-deepest lake in Africa. The lake is dotted with 29 islands.",
    image: "/Lake-Bunyonyi.webp",
    activities: [
      "Canoeing",
      "Swimming",
      "Bird watching",
      "Community visits",
      "Cultural tours",
      "Boat cruises",
      "Fishing"
    ]
  },
  {
    name: "Mabira Forest Reserve",
    description: "Mabira Forest Reserve is located in central Uganda and is one of the largest forests in the country.",
    image: "/mabira-forest.webp",
    activities: [
      "Hiking",
      "Bird watching",
      "Nature walks",
      "Community visits",
      "Guided tours",
      "Zip lining",
      "Forest walks"
    ]
  },
  {
    name: "Amabere Ga NyinaMwiru",
    description: "Amabere Ga NyinaMwiru is a cultural site located in western Uganda.",
    image: "/Amabere-Ga-Nyina-Mwiru.webp",
    activities: [
      "Cultural tours",
      "Community visits",
      "Traditional dance performances",
      "Music performances",
      "Craft workshops"
    ]
  },
  {
    name: "The Baha'i Temple",
    description: "The Baha'i Temple is located in Kampala, Uganda, and is one of the most beautiful temples in Africa.",
    image: "/bahai.png",
    activities: [
      "Guided tours",
      "Community visits",
      "Cultural performances",
      "Music performances",
      "Meditation and reflection"
    ]
  },
  {
    name: "Hot Springs",
    description: "Uganda has many natural hot springs located throughout the country.",
    image: "/sempaya.jpg",
    activities: [
      "Visiting hot springs",
      "Community visits",
      "Cultural tours",
      "Traditional medicine experiences"
    ]
  },
  {
    name: "Uganda Martyrs Shrine – Namugongo",
    description: "The Uganda Martyrs Shrine is a historic site located in Kampala, Uganda.",
    image: "/Namugongo-Catholic-shrine.png",
    activities: [
      "Guided tours",
      "Community visits",
      "Cultural performances",
      "Music performances", "Prayer and reflection"  
    ]
  },
];

export default function ExploreUganda() {
  const { t } = useLanguage();
  
  return (
    <div className="min-h-screen bg-gray-50">
      <Helmet>
        <title>{t("exploreUganda")} - National Parks & Wildlife</title>
        <meta name="description" content="Discover Uganda's stunning national parks, wildlife, and natural attractions." />
      </Helmet>

      <PageBanner
        title={t("exploreUganda")}
        description="Discover the Pearl of Africa's magnificent national parks and wildlife sanctuaries"
        backgroundImage="/5O5A9186.JPG"
      />

      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16">
        <div className="space-y-16">
          <motion.div 
            initial={{ opacity: 0, y: 20 }}
            animate={{ opacity: 1, y: 0 }}
            className="text-center mb-12"
          >
            <p className="text-xl md:text-2xl leading-relaxed max-w-4xl mx-auto text-gray-700 mb-12">
              <span className="font-semibold text-green-700">Welcome to Uganda</span>, the Pearl of Africa, home to breathtaking landscapes, incredible biodiversity, and some of the most incredible wildlife experiences on the planet. Explore our diverse national parks and wildlife reserves.
            </p>
          </motion.div>
          
          <div className="text-center mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 font-heading">National Parks</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              <span className="font-semibold">Uganda's National Parks</span> offer diverse landscapes and incredible wildlife experiences,
              from mountain gorilla encounters to spectacular savanna safaris.
            </p>
          </div>

          {nationalParks.map((park, index) => (
            <motion.div
              key={park.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              className={`flex flex-col ${
                index % 2 === 0 ? 'lg:flex-row' : 'lg:flex-row-reverse'
              } gap-8 items-center`}
            >
              <div className="lg:w-1/2">
                <div className="relative h-[400px] rounded-2xl overflow-hidden shadow-xl">
                  <OptimizedImage
                    src={park.image}
                    alt={park.name}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>

              <div className="lg:w-1/2 space-y-6">
                <h2 className="text-3xl font-bold text-gray-900 font-heading">{park.name}</h2>
                <p className="text-lg text-gray-600">{park.description}</p>

                <h3 className="text-xl font-semibold text-gray-800 font-heading">Activities</h3>
                <div className="grid grid-cols-2 gap-4">
                  {park.activities.map((activity, i) => (
                    <div
                      key={i}
                      className="flex items-center gap-2 text-gray-700"
                    >
                      <div className="w-1.5 h-1.5 bg-primary rounded-full" />
                      <span>{activity}</span>
                    </div>
                  ))}
                </div>

                <Button
                  className="mt-6"
                  onClick={() => window.location.href = `/destinations#${park.name.toLowerCase().replace(/\s+/g, '-')}`}
                >
                  View Tours
                </Button>
              </div>
            </motion.div>
          ))}

          <div className="text-center mt-24 mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 font-heading">Uganda's Game Reserves</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Explore Uganda's wildlife reserves, where unique ecosystems support diverse species
              and offer intimate wildlife viewing experiences away from the crowds.
            </p>
          </div>

          {wildlifeReserves.map((reserve, index) => (
            <motion.div
              key={reserve.name}
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              className="rounded-2xl shadow-lg overflow-hidden bg-white"
            >
              <div className="grid grid-cols-1 lg:grid-cols-2">
                <div className="relative h-[400px]">
                  <OptimizedImage
                    src={reserve.image}
                    alt={reserve.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-8 lg:p-12">
                  <h3 className="text-2xl font-bold text-gray-900 mb-4 font-heading">{reserve.name}</h3>
                  <p className="text-gray-600 mb-6">{reserve.description}</p>

                  <div className="space-y-6">
                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Activities</h4>
                      <div className="flex flex-wrap gap-2">
                        {reserve.activities.map((activity, i) => (
                          <span
                            key={i}
                            className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                          >
                            {activity}
                          </span>
                        ))}
                      </div>
                    </div>

                    <div>
                      <h4 className="text-lg font-semibold text-gray-900 mb-3">Wildlife</h4>
                      <div className="flex flex-wrap gap-2">
                        {reserve.wildlife.map((animal, i) => (
                          <span
                            key={i}
                            className="px-3 py-1 bg-gray-100 text-gray-700 rounded-full text-sm"
                          >
                            {animal}
                          </span>
                        ))}
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            </motion.div>
          ))}

          <div className="text-center mt-24 mb-16">
            <h2 className="text-3xl font-bold text-gray-900 mb-4 font-heading">Other Attractions</h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto">
              Discover Uganda's diverse attractions beyond national parks, from rhino sanctuaries to cultural sites and religious landmarks.
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-8">
            {otherAttractions.map((attraction, index) => (
              <motion.div
                key={attraction.name}
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.2 }}
                className="rounded-2xl shadow-lg overflow-hidden bg-white"
              >
                <div className="relative h-[300px]">
                  <OptimizedImage
                    src={attraction.image}
                    alt={attraction.name}
                    className="w-full h-full object-cover"
                  />
                </div>
                <div className="p-6">
                  <h3 className="text-xl font-bold text-gray-900 mb-3 font-heading">{attraction.name}</h3>
                  <p className="text-gray-600 mb-4">{attraction.description}</p>
                  <div>
                    <h4 className="text-lg font-semibold text-gray-900 mb-3">Activities</h4>
                    <div className="flex flex-wrap gap-2">
                      {attraction.activities.map((activity, i) => (
                        <span
                          key={i}
                          className="px-3 py-1 bg-primary/10 text-primary rounded-full text-sm"
                        >
                          {activity}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </motion.div>
            ))}
          </div>
          
          {/* Popular Uganda Safari Tours Section */}
          <div className="mt-24">
            <div className="text-center mb-16">
              <h2 className="text-3xl font-bold text-gray-900 mb-4 font-heading">Popular Uganda Safari Tours</h2>
              <p className="text-lg text-gray-600 max-w-3xl mx-auto">
                Explore our handcrafted Uganda safari itineraries, each designed to showcase the best wildlife, 
                landscapes, and cultural experiences that the Pearl of Africa has to offer.
              </p>
            </div>
            
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8 mt-12">
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.1 }}
                className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300"
              >
                <Link href="/tours/uganda/murchison-falls-3-day">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src="/murchison-falls.jpg" 
                      alt="Murchison Falls Safari" 
                      className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 font-heading">3-Day Murchison Falls Adventure</h3>
                    <div className="flex items-center mb-3">
                      <Clock className="h-4 w-4 text-primary mr-2" />
                      <p className="text-gray-600">3 Days / 2 Nights</p>
                    </div>
                    <p className="text-gray-600 mb-4">Experience the world's most powerful waterfall and abundant wildlife in Uganda's largest national park.</p>
                    <Button className="w-full bg-primary hover:bg-primary/90 text-white flex items-center justify-center gap-2">
                      View Tour Details
                      <MapPin className="h-4 w-4" />
                    </Button>
                  </div>
                </Link>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.2 }}
                className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300"
              >
                <Link href="/tours/uganda/bwindi-gorilla-5-day">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src="/gorillatrekking.png" 
                      alt="Gorilla Trekking Safari" 
                      className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 font-heading">5-Day Bwindi Gorilla Safari</h3>
                    <div className="flex items-center mb-3">
                      <Clock className="h-4 w-4 text-primary mr-2" />
                      <p className="text-gray-600">5 Days / 4 Nights</p>
                    </div>
                    <p className="text-gray-600 mb-4">Trek through the Impenetrable Forest to encounter endangered mountain gorillas in their natural habitat.</p>
                    <Button className="w-full bg-primary hover:bg-primary/90 text-white flex items-center justify-center gap-2">
                      View Tour Details
                      <MapPin className="h-4 w-4" />
                    </Button>
                  </div>
                </Link>
              </motion.div>
              
              <motion.div
                initial={{ opacity: 0, y: 20 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: 0.3 }}
                className="bg-white rounded-2xl shadow-md overflow-hidden hover:shadow-xl transition-all duration-300"
              >
                <Link href="/tours/uganda/primates-wildlife-8-day">
                  <div className="relative h-48 overflow-hidden">
                    <img 
                      src="/white-rhino-ziwa-rhino-wildlife-ranch.webp" 
                      alt="Primates Safari" 
                      className="w-full h-full object-cover transform hover:scale-105 transition-transform duration-500"
                      loading="lazy"
                      decoding="async"
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 to-transparent opacity-0 hover:opacity-100 transition-opacity duration-300"></div>
                  </div>
                  <div className="p-6">
                    <h3 className="text-xl font-semibold text-gray-900 mb-2 font-heading">8-Day Primates & Wildlife Safari</h3>
                    <div className="flex items-center mb-3">
                      <Clock className="h-4 w-4 text-primary mr-2" />
                      <p className="text-gray-600">8 Days / 7 Nights</p>
                    </div>
                    <p className="text-gray-600 mb-4">Track chimpanzees and encounter diverse primates across Uganda's rainforests and savannahs.</p>
                    <Button className="w-full bg-primary hover:bg-primary/90 text-white flex items-center justify-center gap-2">
                      View Tour Details
                      <MapPin className="h-4 w-4" />
                    </Button>
                  </div>
                </Link>
              </motion.div>
            </div>
            
            <div className="text-center mt-12">
              <Link href="/destinations">
                <Button className="bg-primary hover:bg-primary/90 text-white px-8 py-2 rounded-full shadow-md hover:shadow-lg transition-all duration-300">
                  View All Uganda Tours
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </div>
      
      <PaymentPartners />
    </div>
  );
}